import { useEffect, useCallback, useRef, useMemo } from 'react'
import { collection, query, where, doc, serverTimestamp } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { commitWrites, type BatchWrite } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { eventArtistInterestId, type EventArtistInterest } from '../types'
import { fbt } from 'fbtee'

const EMPTY_SET: ReadonlySet<string> = new Set()

/**
 * The artists the user hearted on one event's lineup — "interested" there, so
 * every event starts clean. A heart also follows the artist app-wide (the
 * `artistInterests` doc, written in the same batch when missing); an un-heart
 * removes only this event's heart and never unfollows.
 */
export function useEventArtistInterests(
  userId: string | null,
  eventId: string,
  options: { followedArtistIds: ReadonlySet<string>; onMarked?: () => void },
) {
  const ref = userId
    ? (query(
        collection(db, 'eventArtistInterests'),
        where('userId', '==', userId),
        where('eventId', '==', eventId),
      ) as Query<EventArtistInterest>)
    : null
  const { data, loading } = useFirestoreCollection<EventArtistInterest>(ref)

  const interestedArtistIds = useMemo(
    () => (userId ? new Set(data.map(d => d.artistId)) : (EMPTY_SET as Set<string>)),
    [userId, data],
  )

  // Latest values for callbacks that outlive a render.
  const latest = useRef({ interestedArtistIds, followed: options.followedArtistIds, onMarked: options.onMarked })
  useEffect(() => {
    latest.current = { interestedArtistIds, followed: options.followedArtistIds, onMarked: options.onMarked }
  })

  // A deterministic id plus an idempotent update rule make re-marking an
  // artist another device already hearted a harmless overwrite.
  const markWrites = useCallback((uid: string, artistIds: string[]): BatchWrite[] =>
    artistIds.flatMap(artistId => [
      {
        kind: 'set' as const,
        ref: doc(db, 'eventArtistInterests', eventArtistInterestId(uid, eventId, artistId)),
        data: { userId: uid, eventId, artistId, createdAt: serverTimestamp() },
      },
      ...(latest.current.followed.has(artistId)
        ? []
        : [{ kind: 'set' as const, ref: doc(collection(db, 'artistInterests')), data: { userId: uid, artistId } }]),
    ]), [eventId])

  /** Hearts every given artist for this event (add-only). Resolves to how many were newly hearted; throws after toasting on failure. */
  const markArtists = useCallback(
    async (artistIds: string[], errorMessage?: string): Promise<number> => {
      if (!userId) return 0
      const toMark = [...new Set(artistIds)].filter(id => !latest.current.interestedArtistIds.has(id))
      if (toMark.length === 0) return 0
      await runMutation(errorMessage ?? fbt("Couldn't update artist interest", 'Error toast when toggling interest in an artist fails'), async () => {
        await commitWrites(db, markWrites(userId, toMark))
        latest.current.onMarked?.()
        track('artist_interest_toggled', {
          marked: true,
          event_id: eventId,
          artist_id: toMark[0],
          artist_count: toMark.length,
        })
      })
      return toMark.length
    },
    [userId, eventId, markWrites],
  )

  const toggleArtistInterest = useCallback(
    async (artistId: string) => {
      if (!userId) return
      const marked = !latest.current.interestedArtistIds.has(artistId)
      await runMutation(fbt("Couldn't update artist interest", 'Error toast when toggling interest in an artist fails'), async () => {
        await commitWrites(db, marked
          ? markWrites(userId, [artistId])
          : [{ kind: 'delete', ref: doc(db, 'eventArtistInterests', eventArtistInterestId(userId, eventId, artistId)) }])
        if (marked) latest.current.onMarked?.()
        track('artist_interest_toggled', { marked, event_id: eventId, artist_id: artistId })
      })
    },
    [userId, eventId, markWrites],
  )

  return { interestedArtistIds, loading: !!userId && loading, toggleArtistInterest, markArtists }
}
